<script setup lang="ts">
import { ref, useId } from 'vue';

import { PhxsgStack as PhxsgCoreStack } from '@sg-core/src/View/Components/Atoms/Stack';

import { PhxsgButton } from '../../Atoms/Button';
import { PhxsgHeading } from '../../Atoms/Heading';
import { PhxsgPicture } from '../../Atoms/Picture';
import { PhxsgText } from '../../Atoms/Text';

import { useColumnReveal } from './use-column-reveal';
import { useLayoutMeasurement } from './use-layout-measurement';

import type { CollectionsBlockItem } from './phxsg-collections-block.vue';

const EXPANDED_BASIS_PROPERTY = '--_comp-collections-block-expanded-basis';

const { items } = defineProps<{
  items: CollectionsBlockItem[];
}>();

const id = useId();
const rootRef = ref<HTMLElement | null>(null);
const selectedIndex = ref<number | null>(null);
const detailsHeight = ref(0);
const thumbnailInfoHeight = ref(0);

useColumnReveal(rootRef);
useLayoutMeasurement(rootRef, updateLayoutHeights);

function selectColumn(index: number): void {
  selectedIndex.value = index;
}

function updateLayoutHeights(): void {
  const root = rootRef.value;

  if (!root) {
    return;
  }

  const expandedRatio = parseFloat(getComputedStyle(root).getPropertyValue(EXPANDED_BASIS_PROPERTY)) / 100;
  const expandedWidth = root.getBoundingClientRect().width * expandedRatio;
  const details = [...root.querySelectorAll<HTMLElement>('.phxsg-collections-block-desktop__details')];
  const thumbnailInfos = [...root.querySelectorAll<HTMLElement>('.phxsg-collections-block-desktop__thumbnail-info')];

  details.forEach((item) => {
    item.style.width = `${expandedWidth}px`;
    item.style.height = 'auto';
  });
  thumbnailInfos.forEach((item) => {
    item.style.minHeight = '0';
  });

  detailsHeight.value = Math.max(0, ...details.map((item) => Math.ceil(item.getBoundingClientRect().height)));
  thumbnailInfoHeight.value = Math.max(0, ...thumbnailInfos.map((item) => item.scrollHeight));

  details.forEach((item) => {
    item.style.width = '';
    item.style.height = '';
  });
  thumbnailInfos.forEach((item) => {
    item.style.minHeight = '';
  });
}
</script>

<template>
  <div
    ref="rootRef"
    class="phxsg-collections-block-desktop phxsg-collections-block-desktop--alt"
    :style="{
      '--_comp-collections-block-details-height': `${detailsHeight}px`,
      '--_comp-collections-block-thumbnail-info-height': `${thumbnailInfoHeight}px`,
    }"
  >
    <div
      v-for="(item, index) in items"
      :key="`${item.title}-${index}`"
      :class="[
        'phxsg-collections-block-desktop__column',
        { 'phxsg-collections-block-desktop__column--expanded': selectedIndex === index },
      ]"
    >
      <div class="phxsg-collections-block-desktop__content">
        <button
          type="button"
          class="phxsg-collections-block-desktop__toggle"
          :aria-expanded="selectedIndex === index"
          :aria-controls="`${id}-details-${index}`"
          @click="selectColumn(index)"
        >
          {{ item.title }}
        </button>

        <PhxsgCoreStack
          :id="`${id}-details-${index}`"
          spacing="4"
          :class="[
            'phxsg-collections-block-desktop__details',
            { 'phxsg-collections-block-desktop__details--hidden': selectedIndex !== index },
          ]"
          :aria-hidden="selectedIndex !== index"
        >
          <PhxsgHeading use="h4">{{ item.title }}</PhxsgHeading>

          <PhxsgText v-if="item.text" use="p">
            {{ item.text }}
          </PhxsgText>
        </PhxsgCoreStack>

        <div class="phxsg-collections-block-desktop__thumbnail-image-wrapper">
          <PhxsgPicture v-bind="item.thumbnail" type="full" class="phxsg-collections-block-desktop__image" />
        </div>

        <div class="phxsg-collections-block-desktop__thumbnail-info">
          <Transition name="phxsg-collections-block-fade-thumbnail-info" mode="out-in">
            <PhxsgText v-if="selectedIndex !== index" key="title">
              {{ item.title }}
            </PhxsgText>

            <PhxsgButton v-else-if="item.cta" key="action" use="a" :href="item.cta.href" variant="fade">
              {{ item.cta.label }}
            </PhxsgButton>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>
